import React, { createContext, useEffect, useMemo, useState } from 'react';

export const AuthContext = createContext();

const STORAGE_KEY = 'faan_coop_auth_user';

export function AuthProvider({ children }) {
  const [user, setUser] = useState(() => {
    try {
      const saved = localStorage.getItem(STORAGE_KEY);
      return saved ? JSON.parse(saved) : null;
    } catch (error) {
      console.error('Failed to read auth user from storage:', error);
      return null;
    }
  });

  useEffect(() => {
    try {
      if (user) {
        localStorage.setItem(STORAGE_KEY, JSON.stringify(user));
      } else {
        localStorage.removeItem(STORAGE_KEY);
      }
    } catch (error) {
      console.error('Failed to persist auth user to storage:', error);
    }
  }, [user]);

  const login = (role, credentials = {}) => {
    const username =
      credentials.username?.trim() ||
      credentials.email?.trim() ||
      'User';

    const safeRole = role === 'admin' ? 'admin' : 'member';

    const nextUser = {
      id:
        safeRole === 'admin'
          ? 'admin-demo-001'
          : `member-${Date.now()}`,
      name: username,
      role: safeRole,
      email: credentials.email || '',
      staffId: credentials.staffId || '',
      isAuthenticated: true,
      lastLoginAt: new Date().toISOString(),
    };

    setUser(nextUser);
    return nextUser;
  };

  const logout = () => {
    setUser(null);
  };

  const updateUser = (updates = {}) => {
    setUser((prev) => {
      if (!prev) return prev;
      return { ...prev, ...updates };
    });
  };

  const isAdmin = user?.role === 'admin';
  const isMember = user?.role === 'member';

  const value = useMemo(
    () => ({
      user,
      login,
      logout,
      updateUser,
      isAuthenticated: !!user,
      isAdmin,
      isMember,
    }),
    [user, isAdmin, isMember]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}